import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SsoIntegrationProviderPresetEnum,
  useSsoIntegrationPresetDefaultsLazyQuery,
} from 'graphql-common';
import { FormRef } from '@lib/components/ReactHookForm/FormContainer';
import { SsoIntegrationFieldNames } from './constants';
import getSsoPresetLabel from './getSsoPresetLabel';
import {
  MappingRow,
  PresetDefaultsData,
  buildMappingRowsFromDefaults,
  mergeMappingRowsWithDefaults,
} from './presetDefaults';

type UsePresetDefaultsArgs = {
  formRef: React.RefObject<FormRef | undefined>;
};

export default function usePresetDefaults({ formRef }: UsePresetDefaultsArgs) {
  const { t } = useTranslation();
  const [fetchPresetDefaults] = useSsoIntegrationPresetDefaultsLazyQuery();

  const handlePresetChange = useCallback(
    (preset: string) => {
      const presetLabel = getSsoPresetLabel(
        preset as SsoIntegrationProviderPresetEnum,
        t,
      );
      if (formRef.current?.setValue) {
        formRef.current.setValue(SsoIntegrationFieldNames.Name, presetLabel, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }

      fetchPresetDefaults({
        variables: {
          preset: preset as SsoIntegrationProviderPresetEnum,
        },
      }).then((result) => {
        const defaults = result.data?.data as PresetDefaultsData | undefined;
        if (!defaults) return;

        const rows = buildMappingRowsFromDefaults(defaults);

        if (formRef.current?.setValue) {
          formRef.current.setValue(
            SsoIntegrationFieldNames.AttributeMapping,
            rows,
            { shouldDirty: true },
          );
        }
      });
    },
    [fetchPresetDefaults, formRef, t],
  );

  const applyPresetDefaults = useCallback(
    (preset: string) => {
      fetchPresetDefaults({
        variables: {
          preset: preset as SsoIntegrationProviderPresetEnum,
        },
      }).then((result) => {
        const defaults = result.data?.data as PresetDefaultsData | undefined;
        if (!defaults) return;

        const currentRows =
          (formRef.current?.values?.[
            SsoIntegrationFieldNames.AttributeMapping
          ] as MappingRow[] | undefined) || [];
        const mergedRows = mergeMappingRowsWithDefaults(currentRows, defaults);

        if (formRef.current?.setValue) {
          formRef.current.setValue(
            SsoIntegrationFieldNames.AttributeMapping,
            mergedRows,
          );
          formRef.current.reset(undefined, { keepValues: true });
        }
      });
    },
    [fetchPresetDefaults, formRef],
  );

  return {
    handlePresetChange,
    applyPresetDefaults,
  };
}
